// Global Instructions Rule Applied!

import { TableFilterOptionsResponse } from '../../../state_manager/rpc_models/get_table_filter_options_response.ts'
import {
  FundraisingPaginatedResponse,
  fetchFundraisingFilterOptions,
  fetchFundraisingPaginated,
  fetchFundraisingFiltered
} from '../services/fundraisingService.ts'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import {
  setFundraising,
  setFilterOptions,
  setActiveFilters,
  setActiveSorting,
  clearActiveFilters,
  appendFundraising
} from '../../../state_manager/state_update_actions/fundraising/index.ts'
import { FilterValue, SortConfig } from '../../../state_manager/store_models/fundraising.ts'
import type { AppDispatch } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import {
  mergeUserPreferenceRowData,
  removeUserPreferenceForMenuItem
} from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import {
  areFilterValuesEqual,
  DEFAULT_FUNDRAISING_TABLE_FILTERS,
  areSortConfigsEqual,
  PREF_TABLE_COLUMN_KEYS,
  PREF_TABLE_FILTERS,
  PREF_TABLE_SORTING,
  resolveTableFiltersFromPreferenceRow,
  resolveTableSortingFromPreferenceRow
} from '../../global_configs/tableViewPreferences.ts'
import { useReportingDefaultSorting } from '../../global_configs/useReportingDefaultSorting.ts'
import { MENU_ITEM_KEY_FUNDRAISING } from '../../../state_manager/globals.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import { useFundraisingReportId } from '../../../state_manager/hooks/useFundraisingReportId.ts'

export const useFundraisingData = () => {
  const dispatch = useDispatch<AppDispatch>()

  const { menuItemId, preferenceRow, persistPreference, deletePreference } = useMenuItemUserPreference(
    MENU_ITEM_KEY_FUNDRAISING
  )

  const fundraisingReportId = useFundraisingReportId()
  const defaultTableSorting = useReportingDefaultSorting(fundraisingReportId)

  const [fundraisingDataLoading, setFundraisingDataLoading] = useState(false)
  const [fundraisingFilterOptionsLoading, setFundraisingFilterOptionsLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [prefsReady, setPrefsReady] = useState(false)
  const skipPersistRef = useRef(true)

  const fundraisingData = useSelector((state: any) => state.fundraising.data)
  const fundraisingFilterOptions = useSelector((state: any) => state.fundraising.filterOptions)
  const activeFilters = useSelector((state: any) => state.fundraising.activeFilters)
  const activeSorting = useSelector((state: any) => state.fundraising.activeSorting)
  const hasMore = useSelector((state: any) => state.fundraising.hasMore ?? false)
  const currentPage = useSelector((state: any) => state.fundraising.currentPage ?? 1)

  const menuItems = useSelector((state: any) => state.globalConfigs?.menu_items ?? [])

  useEffect(() => {
    if (menuItems.length === 0) return
    if (!menuItemId) {
      dispatch(setActiveFilters({ filters: DEFAULT_FUNDRAISING_TABLE_FILTERS }))
      dispatch(setActiveSorting({ sorting: defaultTableSorting }))
      setPrefsReady(true)
      return
    }
    const filters = resolveTableFiltersFromPreferenceRow(preferenceRow, DEFAULT_FUNDRAISING_TABLE_FILTERS)
    const sorting = resolveTableSortingFromPreferenceRow(preferenceRow, defaultTableSorting)
    if (!areFilterValuesEqual(filters, activeFilters)) {
      dispatch(setActiveFilters({ filters }))
    }
    if (!areSortConfigsEqual(sorting, activeSorting)) {
      dispatch(setActiveSorting({ sorting }))
    }
    setPrefsReady(true)
  }, [menuItemId, menuItems.length, preferenceRow, defaultTableSorting, dispatch])

  const handleFetchFilterOptions = useCallback(async () => {
    try {
      setFundraisingFilterOptionsLoading(true)
      const currentToken = getAccessToken(store.getState().auth)
      const response: TableFilterOptionsResponse = await fetchFundraisingFilterOptions(currentToken)
      dispatch(setFilterOptions({ response }))
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching filter options:', e)
      }
    } finally {
      setFundraisingFilterOptionsLoading(false)
    }
  }, [dispatch])

  const handleFetchFundraising = useCallback(
    async (sorting: SortConfig[] = [], filters?: FilterValue[]) => {
      try {
        setFundraisingDataLoading(true)
        const currentToken = getAccessToken(store.getState().auth)
        const hasFilters = filters && filters.length > 0
        const hasSorting = sorting.length > 0

        const response: FundraisingPaginatedResponse =
          hasFilters || hasSorting
            ? await fetchFundraisingFiltered(sorting, filters || [], 1, null, currentToken, fundraisingReportId)
            : await fetchFundraisingPaginated(1, null, currentToken, fundraisingReportId)

        dispatch(setFundraising({ response }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching prospects:', e)
        }
      } finally {
        setFundraisingDataLoading(false)
      }
    },
    [dispatch, fundraisingReportId]
  )

  const handleSetFilters = useCallback(
    (filters: FilterValue[]) => {
      if (areFilterValuesEqual(filters, activeFilters)) return
      dispatch(setActiveFilters({ filters }))
    },
    [dispatch, activeFilters]
  )

  const handleSetSorting = useCallback(
    (sorting: SortConfig[]) => {
      if (areSortConfigsEqual(sorting, activeSorting)) return
      dispatch(setActiveSorting({ sorting }))
    },
    [dispatch, activeSorting]
  )

  const handleClearFilters = useCallback(() => {
    dispatch(clearActiveFilters())
  }, [dispatch])

  const handleResetFilters = useCallback(() => {
    if (!menuItemId) {
      dispatch(setActiveFilters({ filters: DEFAULT_FUNDRAISING_TABLE_FILTERS }))
      return
    }
    void (async () => {
      const result = await deletePreference()
      if (!result.success) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to delete prospects table preferences:', result.error)
        }
        return
      }
      dispatch(removeUserPreferenceForMenuItem(menuItemId))
      skipPersistRef.current = true
      dispatch(setActiveFilters({ filters: DEFAULT_FUNDRAISING_TABLE_FILTERS }))
      dispatch(setActiveSorting({ sorting: defaultTableSorting }))
    })()
  }, [dispatch, menuItemId, deletePreference, defaultTableSorting])

  const handleClearSorting = useCallback(() => {
    dispatch(setActiveSorting({ sorting: defaultTableSorting }))
  }, [dispatch, defaultTableSorting])

  const persistMergedPreference = useCallback(
    async (extra: Record<string, unknown> = {}) => {
      if (!menuItemId) return { success: false as const, error: 'No menu item' }
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const hadExistingRow = reports.some((p) => p.menu_item_id === menuItemId)
      const row = reports.find((p) => p.menu_item_id === menuItemId)
      const merged: Record<string, unknown> = {
        ...(row?.data ?? {}),
        ...extra,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        if (hadExistingRow) {
          dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        } else {
          const user = store.getState().auth.user
          if (user) {
            await fetchGlobalConfigsThunk(user)(dispatch)
          }
        }
      }
      return result
    },
    [menuItemId, activeFilters, activeSorting, persistPreference, dispatch]
  )

  const persistColumnFieldOrder = useCallback(
    async (fieldNames: string[]) => {
      return persistMergedPreference({ [PREF_TABLE_COLUMN_KEYS]: fieldNames })
    },
    [persistMergedPreference]
  )

  const resetColumnLayoutToDefault = useCallback(async () => {
    if (!menuItemId) return { success: false as const, error: 'No menu item' }
    const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
    const hadExistingRow = reports.some((p) => p.menu_item_id === menuItemId)
    const row = reports.find((p) => p.menu_item_id === menuItemId)
    const base: Record<string, unknown> = { ...(row?.data ?? {}) }
    delete base[PREF_TABLE_COLUMN_KEYS]
    const merged: Record<string, unknown> = {
      ...base,
      [PREF_TABLE_FILTERS]: activeFilters,
      [PREF_TABLE_SORTING]: activeSorting
    }
    const result = await persistPreference(undefined, merged)
    if (result.success) {
      if (hadExistingRow) {
        dispatch(mergeUserPreferenceRowData(menuItemId, merged))
      } else {
        const user = store.getState().auth.user
        if (user) {
          await fetchGlobalConfigsThunk(user)(dispatch)
        }
      }
    }
    return result
  }, [menuItemId, activeFilters, activeSorting, persistPreference, dispatch])

  const handleLoadMore = useCallback(
    async (page: number, pageSize: number) => {
      if (loadingMore || !hasMore) return

      try {
        setLoadingMore(true)
        const currentToken = getAccessToken(store.getState().auth)
        const hasFilters = activeFilters && activeFilters.length > 0
        const hasSorting = Array.isArray(activeSorting) && activeSorting.length > 0

        let response: FundraisingPaginatedResponse
        if (hasFilters || hasSorting) {
          response = await fetchFundraisingFiltered(
            activeSorting,
            activeFilters,
            page,
            pageSize,
            currentToken,
            fundraisingReportId
          )
        } else {
          response = await fetchFundraisingPaginated(page, pageSize, currentToken, fundraisingReportId)
        }

        dispatch(appendFundraising({ response }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error loading more prospects:', e)
        }
      } finally {
        setLoadingMore(false)
      }
    },
    [dispatch, activeFilters, activeSorting, hasMore, loadingMore, fundraisingReportId]
  )

  useEffect(() => {
    if (!prefsReady) return
    const currentToken = getAccessToken(store.getState().auth)
    if (currentToken) {
      handleFetchFilterOptions()
      handleFetchFundraising(activeSorting, activeFilters)
    } else {
      setFundraisingFilterOptionsLoading(false)
    }
  }, [prefsReady, activeFilters, activeSorting, handleFetchFilterOptions, handleFetchFundraising])

  useEffect(() => {
    if (!prefsReady || !menuItemId) return
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }
    void persistMergedPreference().catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to persist prospects table preferences:', err)
      }
    })
  }, [prefsReady, menuItemId, activeFilters, activeSorting, persistMergedPreference])

  return {
    fundraisingFilterOptions: fundraisingFilterOptions.data,
    fundraisingData,
    fundraisingDataLoading,
    fundraisingFilterOptionsLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    handleFetchFundraising,
    handleFetchFilterOptions,
    handleSetFilters,
    handleSetSorting,
    handleClearFilters,
    handleClearSorting,
    handleLoadMore,
    defaultTableFilters: DEFAULT_FUNDRAISING_TABLE_FILTERS,
    defaultTableSorting,
    handleResetFilters,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  }
}
